import React from 'react'
import PropTypes from 'prop-types'
import { Box, useTheme } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useKeyValueData } from './hooks/useKeyValueData'
import WidgetDisplay from './components/WidgetDisplay'
import WidgetLoader from './components/WidgetLoader'
import WidgetError from './components/WidgetError'
import ErrorBoundary from './components/ErrorBoundary'

/**
 * KeyValueWidget Component
 * Displays a real-time value from a configured API key.
 *
 * @component
 * @example
 * const data = { key: 'machine_1.temperature', label: 'Temperature' }
 * return <KeyValueWidget data={data} />
 */
const KeyValueWidget = ({ data }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const key = data?.key

  // Use custom hook for data fetching logic
  const { value, loading, error, refresh } = useKeyValueData(key)

  return (
    <ErrorBoundary>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 2,
          position: 'relative',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          overflow: 'hidden',
          // Use theme shadow or border if needed, keeping consistent with other widgets
          boxShadow: theme.shadows[1]
        }}
      >

        {/* Widget Content */}
        <Box sx={{ flex: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {loading && value === null
            ? (
              <WidgetLoader t_={t_} />
              )
            : error
              ? (
                <WidgetError error={error} onRetry={refresh} t_={t_} />
                )
              : (
                <WidgetDisplay value={value} t_={t_} />
                )}
        </Box>
      </Box>
    </ErrorBoundary>
  )
}

KeyValueWidget.propTypes = {
  data: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string
  })
}

export default KeyValueWidget
